import { BKJIcon } from '@bookjane2/bookjane-design-library';
import { ChangeFunctionType } from '@bookjane2/bookjane-design-library/lib/common.types';
import {
  BKJSSFBarSearchInputWrapper,
  IconWrapper,
  StyledBKJSSFBarSearchInput,
} from 'components/BKJSSFBarSearchInput/BKJSSFBarSearchInput.styled';
import { IconButton } from 'components/IconButton';
import { forwardRef, RefObject, useCallback, useState, useEffect, useRef } from 'react';

export type BKJSSFBarSearchInputVariant = 'modal' | 'page';

interface IBKJSSFBarSearchInputProps {
  disabled?: boolean;
  name: string;
  onChange: ChangeFunctionType;
  onClearSearch: (e: unknown) => void;
  onClick: () => void;
  placeholder: string;
  value: string;
  teamMemberQueryFilterValue: string;
  variant?: BKJSSFBarSearchInputVariant;
}

export const BKJSSFBarSearchInput = forwardRef<HTMLInputElement, IBKJSSFBarSearchInputProps>(
  (props, ref) => {
    const { onClearSearch, value, teamMemberQueryFilterValue, onChange, ...rest } = props;

    const [isFocused, setFocus] = useState<boolean>(false);
    const [localSearchValue, setLocalSearchValue] = useState<string>(teamMemberQueryFilterValue);
    const debounceTimerRef = useRef<number | null>(null);

    useEffect(() => {
      setLocalSearchValue(teamMemberQueryFilterValue);
    }, [teamMemberQueryFilterValue]);

    // Cleanup timer on unmount
    useEffect(() => {
      return () => {
        if (debounceTimerRef.current !== null) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    }, []);

    const onFocused = useCallback(() => {
      if (!isFocused) setFocus(true);
    }, [isFocused]);

    const onBlur = useCallback(() => {
      if (isFocused) setFocus(false);
    }, [isFocused]);

    const handleInputChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;

        // Update displayed value
        setLocalSearchValue(newValue);

        if (debounceTimerRef.current !== null) {
          clearTimeout(debounceTimerRef.current);
        }

        // Debounce onChange
        debounceTimerRef.current = window.setTimeout(() => {
          onChange(event);
        }, 300);
      },
      [onChange],
    );

    const isCloseIconDisplayed = !!localSearchValue || !!value;

    /**
     * @const currentInputFieldValue This is a constant that returns what the user typed if
     * the input ele is focused, otherwise it returns what the selected team member value is.
     * @description If the input is focused we want to show the string that the user typed.
     * Otherwise we can just show the selected TeamMember/Jane's name.
     */
    const currentInputFieldValue = isFocused ? localSearchValue : value;

    return (
      <BKJSSFBarSearchInputWrapper ref={ref as RefObject<HTMLLabelElement>} variant={props.variant}>
        <StyledBKJSSFBarSearchInput
          id="BKJSSFBarSearchInput"
          {...rest}
          onFocus={onFocused}
          onBlur={onBlur}
          onChange={handleInputChange}
          role="combobox"
          type="text"
          value={currentInputFieldValue}
          autoComplete="off"
          variant={props.variant}
        />
        <IconWrapper variant={props.variant}>
          {isCloseIconDisplayed ? (
            <IconButton iconName="CloseBold" onClick={onClearSearch} />
          ) : (
            <BKJIcon iconName="Search" />
          )}
        </IconWrapper>
      </BKJSSFBarSearchInputWrapper>
    );
  },
);
